import { AcademyGuideSummaryText } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/components/GuideCards/AcademyGuideSummaryText";
import { GuideRowSection } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/styles";
import type { FaireAcademyGuide } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/types";
import {
  optimizeImageUrlFillRectangle,
  optimizeImageUrlWidth,
  optimizeImageUrlFillSquare,
} from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Row, Column } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import * as React from "react";
import styled from "styled-components";

export const GuidePageHeader: React.FC<{ guide: FaireAcademyGuide }> = ({
  guide,
}) => {
  const isSSR = useIsSSR();
  const { isMobile } = useViewport();
  const {
    title,
    description,
    readTimeMinutes,
    chapters,
    featuredImage,
    featuredImageAltText,
    backgroundImage,
    likes,
  } = guide;

  const backgroundImageOptimized =
    optimizeImageUrlWidth(
      IImage.build({
        url: backgroundImage,
      }),
      isSSR,
      1600
    ) ?? backgroundImage;

  if (isMobile) {
    return (
      <MobileColumnWrapper $backgroundImage={backgroundImageOptimized}>
        <img
          style={{
            objectFit: "cover",
            width: "100%",
          }}
          src={optimizeImageUrlFillSquare(
            IImage.build({
              url: featuredImage,
            }),
            isSSR,
            400 * 2
          )}
          alt={featuredImageAltText}
        />
        <Column style={{ padding: getSpacing("3x") }}>
          <Typography variant="displaySSerifRegular" as="h1">
            {title}
          </Typography>
          <Spacer height="1x" />
          <Typography>{description}</Typography>
          <Spacer height="2x" />
          <AcademyGuideSummaryText
            numChapters={chapters.length}
            readTimeMinutes={readTimeMinutes}
            likes={likes}
          />
        </Column>
      </MobileColumnWrapper>
    );
  }

  return (
    <RowWithBackground $backgroundImage={backgroundImageOptimized}>
      <ContainerRow justify="center">
        <GuideRowSection>
          <TextColumn justify="center">
            <Typography variant="displayLSerifRegular" as="h1">
              {title}
            </Typography>
            <Spacer height="1x" />
            <Typography variant="subheadingSansRegular">
              {description}
            </Typography>
            <Spacer height="2x" />
            <AcademyGuideSummaryText
              numChapters={chapters.length}
              readTimeMinutes={readTimeMinutes}
              likes={likes}
            />
          </TextColumn>
        </GuideRowSection>
        <Spacer width="2x" />
        <DynamicImage
          src={optimizeImageUrlFillRectangle(
            IImage.build({
              url: featuredImage,
            }),
            isSSR,
            500 * 2,
            408 * 2
          )}
          // Note: percents are based on calculation to align image with hashmarks of background image
          width="28.93%"
          height="62.58%"
          alt={featuredImageAltText}
        />
      </ContainerRow>
    </RowWithBackground>
  );
};

// Use of padding-top is a hack to get a background image to scale the size of the div
const RowWithBackground = styled.div<{ $backgroundImage: string }>`
  background-image: url("${({ $backgroundImage }) => $backgroundImage}");
  background-size: contain;
  background-repeat: no-repeat;
  position: relative;
  width: 100%;
  height: 0;
  padding-top: 37.72%;
`;

// Since we use the padding hack we have to use a height of 0 for the background div
// To make it a flex row we have to add an absolute positioned Row
const ContainerRow = styled(Row)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

// The image must have a dynamic position to properly stay within the background
const DynamicImage = styled.img`
  object-fit: cover;
  position: absolute;
  top: 18.71%;
  right: 12.21%;
`;

const TextColumn = styled(Column)`
  max-width: 57%;
`;

const MobileColumnWrapper = styled(Column)<{ $backgroundImage: string }>`
  width: 100%;
  background-image: url("${({ $backgroundImage }) => $backgroundImage}");
`;
